/**
 * Service for interacting with Glossary Management APIs (Tenant and Category)
 *
 * Uses fetchWithAuth for automatic 401 handling and token refresh.
 */

import { fetchWithAuth } from '../utils/fetchWithAuth';

/**
 * Deduplicate glossary entries by (sourceLanguage, targetLanguage, sourceText) key.
 * Keeps the last occurrence of each duplicate.
 */
const deduplicateEntries = (entries) => {
    if (!Array.isArray(entries) || entries.length === 0) return entries;
    const seen = new Map();
    for (const entry of entries) {
        const key = `${entry.sourceLanguage}:${entry.targetLanguage}:${entry.sourceText}`;
        seen.set(key, entry);
    }
    return Array.from(seen.values());
};

const getBaseUrl = () => {
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const baseUrl = process.env.REACT_APP_FUNCTIONS_URL || (isLocalDev ? 'http://localhost:7071/api' : 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api');
    return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
};

const glossaryService = {
    // --- Tenant Glossary Methods ---

    /**
     * Create a new tenant glossary
     */
    createGlossary: async (glossaryData) => {
        const url = `${getBaseUrl()}/glossary/tenant/create`;
        const payload = glossaryData.entries
            ? { ...glossaryData, entries: deduplicateEntries(glossaryData.entries) }
            : glossaryData;
        const response = await fetchWithAuth(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to create glossary: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Update an existing tenant glossary
     */
    updateGlossary: async (glossaryData) => {
        const url = `${getBaseUrl()}/glossary/tenant/update`;
        const payload = glossaryData.entries
            ? { ...glossaryData, entries: deduplicateEntries(glossaryData.entries) }
            : glossaryData;
        const response = await fetchWithAuth(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to update glossary: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Get a specific tenant glossary
     */
    getGlossary: async (tenantId, glossaryName) => {
        const params = new URLSearchParams({ tenantId, glossaryName });
        const url = `${getBaseUrl()}/glossary/tenant/get?${params.toString()}`;

        const response = await fetchWithAuth(url, {
            method: 'GET'
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to get glossary: ${response.status}`);
        }

        return response.json();
    },

    /**
     * List all glossaries for a tenant
     */
    listGlossaries: async (tenantId) => {
        const params = new URLSearchParams({ tenantId });
        const url = `${getBaseUrl()}/glossary/tenant/list?${params.toString()}`;

        const response = await fetchWithAuth(url, {
            method: 'GET'
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to list glossaries: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Delete a tenant glossary
     */
    deleteGlossary: async (tenantId, glossaryName) => {
        const params = new URLSearchParams({ tenantId, glossaryName });
        const url = `${getBaseUrl()}/glossary/tenant/delete?${params.toString()}`;

        const response = await fetchWithAuth(url, {
            method: 'DELETE'
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to delete glossary: ${response.status}`);
        }

        return response.json();
    },

    // --- Shared Glossary Methods ---

    /**
     * Create a new shared glossary
     */
    createSharedGlossary: async (sharedGlossaryData) => {
        const url = `${getBaseUrl()}/glossary/shared/create`;
        const payload = sharedGlossaryData.entries
            ? { ...sharedGlossaryData, entries: deduplicateEntries(sharedGlossaryData.entries) }
            : sharedGlossaryData;
        const response = await fetchWithAuth(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to create shared glossary: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Update an existing shared glossary
     */
    updateSharedGlossary: async (sharedGlossaryName, sharedGlossaryData) => {
        const url = `${getBaseUrl()}/glossary/shared/${encodeURIComponent(sharedGlossaryName)}`;
        const payload = sharedGlossaryData.entries
            ? { ...sharedGlossaryData, entries: deduplicateEntries(sharedGlossaryData.entries) }
            : sharedGlossaryData;
        const response = await fetchWithAuth(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to update shared glossary: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Get a specific shared glossary
     */
    getSharedGlossary: async (sharedGlossaryName) => {
        const url = `${getBaseUrl()}/glossary/shared/${encodeURIComponent(sharedGlossaryName)}`;
        const response = await fetchWithAuth(url, {
            method: 'GET'
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to get shared glossary: ${response.status}`);
        }

        return response.json();
    },

    /**
     * List all shared glossaries
     * Note: Route changed from /glossary/shared/list to /glossaries/shared to avoid conflict with GET /glossary/shared/{name}
     */
    listSharedGlossaries: async () => {
        const url = `${getBaseUrl()}/glossaries/shared`;
        const response = await fetchWithAuth(url, {
            method: 'GET'
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to list shared glossaries: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Delete a shared glossary
     */
    deleteSharedGlossary: async (sharedGlossaryName) => {
        const url = `${getBaseUrl()}/glossary/shared/${encodeURIComponent(sharedGlossaryName)}`;
        const response = await fetchWithAuth(url, {
            method: 'DELETE'
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to delete shared glossary: ${response.status}`);
        }

        return response.json();
    },

    /**
     * List all category/shared glossaries (alias for listSharedGlossaries)
     */
    listCategoryGlossaries: async () => {
        const url = `${getBaseUrl()}/glossaries/shared`;
        const response = await fetchWithAuth(url, {
            method: 'GET'
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to list category glossaries: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Generate a glossary from a document using AI pipeline
     * @param {File} file - Document file (PDF, JPEG, PNG)
     * @param {string[]} targetLanguages - Array of target language codes
     * @param {string} sourceLanguage - Optional source language code
     * @param {AbortSignal} signal - Optional AbortController signal for cancellation
     */
    generateGlossary: async (file, targetLanguages, sourceLanguage, signal) => {
        const url = `${getBaseUrl()}/glossary/generate`;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('targetLanguages', targetLanguages.join(','));
        if (sourceLanguage) formData.append('sourceLanguage', sourceLanguage);

        // Do NOT set Content-Type — browser sets multipart boundary automatically
        const response = await fetchWithAuth(url, {
            method: 'POST',
            body: formData,
            signal
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Generation failed: ${response.status}`);
        }
        return response.json();
    },

    /**
     * Start async glossary generation from a document using AI pipeline.
     * Returns a jobId immediately; poll getIngestionStatus() for progress.
     * @param {File} file - Document file (PDF, JPEG, PNG)
     * @param {string[]} targetLanguages - Array of target language codes
     * @param {string} sourceLanguage - Optional source language code
     * @param {string} tenantId - Tenant ID for job tracking
     */
    generateGlossaryAsync: async (file, targetLanguages, sourceLanguage, tenantId) => {
        const url = `${getBaseUrl()}/glossary/generate-async`;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('targetLanguages', targetLanguages.join(','));
        formData.append('tenantId', tenantId);
        if (sourceLanguage) formData.append('sourceLanguage', sourceLanguage);

        const response = await fetchWithAuth(url, {
            method: 'POST',
            body: formData
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to start generation: ${response.status}`);
        }
        return response.json();
    },

    /**
     * Poll the status of a glossary ingestion/generation job.
     * @param {string} jobId - The job ID returned by generateGlossaryAsync
     * @param {string} tenantId - Tenant ID
     * @returns {Object} Status response with status, progressPercent, result (when complete)
     */
    getIngestionStatus: async (jobId, tenantId) => {
        const params = new URLSearchParams({ tenantId });
        const url = `${getBaseUrl()}/glossary/ingestion/${encodeURIComponent(jobId)}/status?${params.toString()}`;

        const response = await fetchWithAuth(url, {
            method: 'GET'
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || error.Error || `Failed to get status: ${response.status}`);
        }
        return response.json();
    },

    // --- Public Domain Library Methods ---

    /**
     * Get the flattened terms for a single public medical specialty.
     * Returns { specialtyName, entries: [{ sourceText, targetLanguage, targetText }] }.
     * Used by DomainLibrary.js Terms tab to preview specialty entries.
     * @param {string} specialtyName - e.g. 'radiology', 'cardiology'
     * @returns {Promise<{ specialtyName: string, entries: Array }>}
     */
    getPublicSpecialty: async (specialtyName) => {
        const url = `${getBaseUrl()}/glossary/public/${encodeURIComponent(specialtyName)}`;
        const response = await fetchWithAuth(url, { method: 'GET' });
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to get specialty: ${response.status}`);
        }
        return response.json();
    },

    /**
     * Get the public domain library (pre-built UMLS medical glossaries).
     * @returns {Promise<Array>} Array of specialty objects
     */
    getPublicDomainLibrary: async () => {
        const url = `${getBaseUrl()}/glossary/public/library`;
        const response = await fetchWithAuth(url, { method: 'GET' });
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to get domain library: ${response.status}`);
        }
        return response.json();
    },

    /**
     * Trigger a UMLS medical glossary harvest (SuperAdmin only).
     * Returns the raw Response so callers can inspect status (202, 409, 403).
     * @param {string} umlsVersion - e.g. 'latest' or '2025AB'
     * @param {{ skipUmls?: boolean, skipIcd11?: boolean, skipMesh?: boolean, autoApproveStep2?: boolean }} [options] - Advanced trigger options
     * @returns {Promise<Response>}
     */
    triggerHarvest: async (umlsVersion = 'latest', options = {}) => {
        const params = new URLSearchParams({ umlsVersion });
        if (options.skipUmls) params.set('skipUmls', 'true');
        if (options.skipIcd11) params.set('skipIcd11', 'true');
        if (options.skipMesh) params.set('skipMesh', 'true');
        if (options.autoApproveStep2) params.set('autoApproveStep2', 'true');
        if (options.forceNewRun) params.set('forceNewRun', 'true');
        const url = `${getBaseUrl()}/glossary/public/harvest?${params.toString()}`;
        const response = await fetchWithAuth(url, { method: 'POST' });
        if (!response.ok && response.status !== 409 && response.status !== 403) {
            throw new Error(`Harvest trigger failed: ${response.status} ${response.statusText}`);
        }
        return response;
    },

    /**
     * Get current harvest status. When instanceId is provided, the backend also returns
     * orchestrationStatus from the Durable Task runtime so the UI can detect blob/runtime conflicts.
     * @param {string} [instanceId] - Optional Durable orchestration instance ID (e.g. "harvest-2025AB").
     * @returns {Promise<{ status: string, currentSpecialty: string, specialtiesCompleted: number, specialtiesTotal: number, orchestrationStatus?: string }>}
     */
    getHarvestStatus: async (instanceId) => {
        let url = `${getBaseUrl()}/glossary/public/harvest-status`;
        if (instanceId) {
            url += `?instanceId=${encodeURIComponent(instanceId)}`;
        }
        const response = await fetchWithAuth(url, { method: 'GET' });
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to get harvest status: ${response.status}`);
        }
        return response.json();
    },

    /**
     * Import a public domain glossary into a tenant's glossaries.
     * @param {string} medicalSpecialty - e.g. 'cardiology'
     * @param {string} tenantId - Tenant to import into
     * @returns {Promise<{ success: boolean }>}
     */
    importPublicGlossary: async (medicalSpecialty, tenantId) => {
        const url = `${getBaseUrl()}/glossary/public/import`;
        const response = await fetchWithAuth(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ medicalSpecialty, tenantId })
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to import glossary: ${response.status}`);
        }
        return response.json();
    },

    // --- Harvest Lifecycle Methods (SuperAdmin only) ---

    /**
     * Pause a running harvest orchestration.
     * @param {string} instanceId - Durable orchestration instance ID (e.g. "harvest-2025AB")
     * @returns {Promise<Response>}
     */
    pauseHarvest: async (instanceId) => {
        const url = `${getBaseUrl()}/glossary/public/harvest/${encodeURIComponent(instanceId)}/pause`;
        const response = await fetchWithAuth(url, { method: 'POST' });
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to pause harvest: ${response.status}`);
        }
        return response;
    },

    /**
     * Resume a paused harvest orchestration.
     * @param {string} instanceId - Durable orchestration instance ID
     * @returns {Promise<Response>}
     */
    resumeHarvest: async (instanceId) => {
        const url = `${getBaseUrl()}/glossary/public/harvest/${encodeURIComponent(instanceId)}/resume`;
        const response = await fetchWithAuth(url, { method: 'POST' });
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to resume harvest: ${response.status}`);
        }
        return response;
    },

    /**
     * Stop (terminate) a running or paused harvest orchestration.
     * @param {string} instanceId - Durable orchestration instance ID
     * @returns {Promise<Response>}
     */
    stopHarvest: async (instanceId) => {
        const url = `${getBaseUrl()}/glossary/public/harvest/${encodeURIComponent(instanceId)}/stop`;
        const response = await fetchWithAuth(url, { method: 'POST' });
        // 409 means the orchestration is already in a terminal state — the backend still
        // calls MarkStoppedAsync to reconcile the status blob, so treat it as a soft-success.
        if (!response.ok && response.status !== 409) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to stop harvest: ${response.status}`);
        }
        return response;
    },

    /**
     * Approve Step 2 (LLM batch gap-fill) after reviewing Step 1 statistics.
     * @param {string} instanceId - Durable orchestration instance ID
     * @returns {Promise<Response>}
     */
    approveStep2: async (instanceId) => {
        const url = `${getBaseUrl()}/glossary/public/harvest/${encodeURIComponent(instanceId)}/approve-step2`;
        const response = await fetchWithAuth(url, { method: 'POST' });
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `Failed to approve Step 2: ${response.status}`);
        }
        return response;
    },

    /**
     * Get list of tenants.
     * Migrated to /superadmin/tenants (AdminFunctions.AdminListTenants) — Sprint 3.
     * Response is normalized to the legacy flat-array shape {RowKey, TenantName, ...}
     * so all existing callers continue to work without changes.
     */
    getTenants: async () => {
        const url = `${getBaseUrl()}/superadmin/tenants`;
        console.log('[GlossaryService] Fetching tenants from:', url);

        try {
            const response = await fetchWithAuth(url, {
                method: 'GET'
            });

            console.log('[GlossaryService] GetTenants response status:', response.status);

            if (!response.ok) {
                const error = await response.json().catch(() => ({ error: response.statusText }));
                console.error('[GlossaryService] GetTenants error:', error);
                throw new Error(error.error || `Failed to get tenants: ${response.status}`);
            }

            const data = await response.json();
            console.log('[GlossaryService] GetTenants data:', data);

            // Normalize AdminListTenants response shape { count, tenants: [{tenantId, name, ...}] }
            // to the legacy flat-array shape [{ RowKey, TenantName, ... }] expected by callers.
            if (data && Array.isArray(data.tenants)) {
                return data.tenants.map(t => ({
                    RowKey: t.tenantId,
                    TenantName: t.name,
                    OrganizationName: t.organizationName,
                    LifecycleState: t.lifecycleState,
                    CreatedAt: t.createdAt,
                    PlanTier: t.planTier,
                    PlanId: t.planId,
                    UserCount: t.userCount,
                    Metadata: t.metadata
                }));
            }
            return data;
        } catch (error) {
            console.error('[GlossaryService] GetTenants fetch error:', error);
            throw error;
        }
    }
};

export default glossaryService;
